import {
  CircleDollarSign,
  CodeXml,
  Gift,
  LayoutPanelTop,
  Palette,
  PencilRuler,
  Shirt,
  ShoppingBasket,
  Split,
  SquareStack,
  Truck,
} from "lucide-react";
import React, { ReactNode } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IgTypography } from "@intelligems/ig-theme";
import {
  ScratchCard as ScratchCardType,
  ScratchOfferCard,
} from "@intelligems/ig-types";
import { checkOfferLimit } from "@intelligems/ig-utils/offer";
import { Grid } from "@mui/material";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { OfferLimitReachedDialog } from "@src/features/sharedExperience/components/alertsAndWarnings/OfferLimitReachedDialog";
import { PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { setDialog } from "@src/store/slices/global";

import { ScratchCard } from "./ScratchCard";

interface StartFromScratchCardsProps {
  renderLocation: "offers" | "overview";
}

export const StartFromScratchCards = ({
  renderLocation,
}: StartFromScratchCardsProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const organization = useGetOrg();
  const personalizationsList = useGetExperiencesList({
    category: "personalization",
  });
  const experienceList = useGetExperiencesList({
    category: "experiment",
  });

  const isUnderOfferLimit = checkOfferLimit(
    organization.data?.org,
    personalizationsList.data?.experiencesList || [],
    experienceList.data?.experiencesList
  );

  const offerCards: (ScratchOfferCard & { icon: ReactNode })[] = [
    {
      title: "Amount off Products",
      description: "Discount specific products",
      discountType: "cartDiscount",
      discountMethod: "perItem",
      modType: "cartDiscount",
      id: "cart-discount-products",
      icon: <Shirt color={"#A11D20"} size={24} />,
      backgroundColor: "#FFEDE5",
    },
    {
      title: "Amount off Order",
      description: "Discount the total order amount",
      discountType: "cartDiscount",
      discountMethod: "perOrder",
      modType: "cartDiscount",
      id: "cart-discount-order",
      icon: <ShoppingBasket size={24} color={"#831843"} />,
      backgroundColor: "#FCE7F3",
    },
    {
      title: "Volume Discount",
      description: "Discount in tiers",
      discountType: "tieredDiscount",
      modType: "tieredDiscount",
      id: "tiered-discount",
      icon: <SquareStack color={"#00679B"} size={24} />,
      backgroundColor: "#EAF4FF",
    },
    // {
    //   title: "Buy X Get Y",
    //   description: "Discount specific products or collections of products",
    //   discountType: "cartDiscount",
    //   id: "discount-custom",
    // },
    {
      title: "Free Shipping",
      description: "Offer free shipping on an order",
      discountType: "freeShipping",
      modType: "freeShipping",
      id: "free-shipping",
      icon: <Truck size={24} color={"#136F45"} />,
      backgroundColor: "#E3FFED",
    },
    {
      title: "Free Gift",
      description: "Offer free gifts on an order",
      discountType: "freeGift",
      modType: "freeGift",
      id: "free-gift",
      icon: <Gift size={24} color={"#6D28D9"} />,
      backgroundColor: "#F4F0FF",
    },
  ];

  const overviewCards: (ScratchCardType & { icon: ReactNode })[] = [
    {
      title: "Redirects",
      description: "Redirect audiences from one URL to another",
      modType: "splitUrl",
      id: "split-url",
      icon: <Split color={"#A11D20"} size={24} />,
      backgroundColor: "#FFEDE5",
    },
    {
      title: "Content Edits",
      description: "Edit text, images, and more on specific pages",
      modType: "onsiteEdits",
      id: "onsite-edits",
      icon: <PencilRuler color={"#831843"} size={24} />,
      backgroundColor: "#FCE7F3",
    },
    {
      title: "Styles & Javascript",
      description: "Inject CSS and javascript to modify functionality",
      modType: "onsiteInjections",
      id: "onsite-injections",
      icon: <CodeXml size={24} color={"#00679B"} />,
      backgroundColor: "#EAF4FF",
    },
    {
      title: "Theme Change",
      description: "Change appearance by swapping your Shopify theme",
      modType: "theme",
      id: "theme-change",
      icon: <Palette size={24} color={"#136F45"} />,
      backgroundColor: "#E3FFED",
    },
    {
      title: "Template Changes",
      description: "Overhaul pages by swapping Shopify templates",
      modType: "template",
      id: "template-changes",
      icon: <LayoutPanelTop size={24} color={"#6D28D9"} />,
      backgroundColor: "#F4F0FF",
    },
    {
      title: "Price Change",
      description: "Make specific price changes for products",
      modType: "price",
      id: "price-change",
      icon: <CircleDollarSign size={24} color={"#C026D3"} />,
      backgroundColor: "#FAE8FF",
    },
  ];

  const cards = renderLocation === "offers" ? offerCards : overviewCards;
  const handleClick = (card: ScratchOfferCard | ScratchCardType) => {
    if (renderLocation === "offers") {
      if (!isUnderOfferLimit) {
        dispatch(setDialog({ dialog: "offerLimitReached", open: true }));
        return;
      }

      const offerCard = card as ScratchOfferCard;
      void navigate(PERSONALIZATION_ROUTES.Personalization, {
        state: {
          name: "New Offer",
          showOfferDialog: true,
          discountType: offerCard.discountType,
          discountMethod: offerCard.discountMethod,
          isCreate: true,
          isEdit: false,
        },
      });
    } else {
      void navigate(PERSONALIZATION_ROUTES.Personalization, {
        state: {
          name: `New ${card.title}`,
          modToActivate: (card as ScratchCardType).modType,
        },
      });
    }
  };

  return (
    <Grid container marginTop={8}>
      <IgTypography variant={"h3"} fontWeight={600}>
        Start from scratch
      </IgTypography>
      <Grid
        container
        item
        direction={"row"}
        wrap={"nowrap"}
        marginTop={5}
        columnSpacing={3}
        justifyContent={"space-between"}
      >
        {cards.map(
          (
            card: (ScratchOfferCard | ScratchCardType) & { icon: ReactNode }
          ) => {
            return (
              <ScratchCard
                key={card.id}
                card={card}
                onClick={() => handleClick(card)}
              />
            );
          }
        )}
      </Grid>
    </Grid>
  );
};
